import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CreditCard, Smartphone } from 'lucide-react'
import { useTastePayment } from '../contexts/TastePaymentContext'
import LoadingSpinner from '../../secret/components/ui/LoadingSpinner'
import { CAMPAIGN } from '../config/campaign'

interface TastePaymentFormProps {
  containerId: string
  onPaymentComplete: () => void
  onApplePayComplete?: () => void
  onGooglePayComplete?: () => void
  isProcessing: boolean
  buttonText: string
}

const TastePaymentForm: React.FC<TastePaymentFormProps> = ({
  containerId,
  onPaymentComplete,
  onApplePayComplete,
  onGooglePayComplete,
  isProcessing,
  buttonText,
}) => {
  const {
    paymentError,
    clearError,
    isApplePayAvailable,
    isGooglePayAvailable,
  } = useTastePayment()

  const [selectedMethod, setSelectedMethod] = useState<'card' | 'apple' | 'google'>('card')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [agreedToPreOrder, setAgreedToPreOrder] = useState(false)
  // Set when the user clicks Pay without checking both consents, so we can
  // surface a hint and highlight the missing boxes instead of a dead button.
  const [showConsentHint, setShowConsentHint] = useState(false)

  // Clear errors when payment method changes
  useEffect(() => {
    clearError()
  }, [selectedMethod, clearError])

  // Clear the consent hint as soon as both boxes are checked
  useEffect(() => {
    if (agreedToTerms && agreedToPreOrder) setShowConsentHint(false)
  }, [agreedToTerms, agreedToPreOrder])

  // Handle payment submission
  const handlePayment = async () => {
    clearError()

    // Guard: both consents are required. Instead of a silently-disabled button,
    // surface a clear hint and highlight the unchecked box(es).
    if (!agreedToPreOrder || !agreedToTerms) {
      setShowConsentHint(true)
      // Scroll the acknowledgments into view so the user sees what's missing.
      document.getElementById('taste-consent-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    switch (selectedMethod) {
      case 'apple':
        onApplePayComplete?.()
        break
      case 'google':
        onGooglePayComplete?.()
        break
      default:
        onPaymentComplete()
    }
  }

  return (
    <div>
      {/* Payment Method Selection */}
      <div className="space-y-3 mb-6">
        <h3 className="text-lg font-semibold !text-white">Payment Method</h3>

        <div className="grid gap-3">
          {/* Card Payment */}
          <button
            type="button"
            onClick={() => setSelectedMethod('card')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              selectedMethod === 'card'
                ? 'border-[#962831] bg-white/10'
                : 'border-white/20 hover:border-[#962831]'
            }`}
          >
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 mr-3 text-[#962831]" />
              <span className="!text-white font-bold text-lg">Credit or Debit Card</span>
            </div>
          </button>

          {/* Apple Pay */}
          {isApplePayAvailable ? (
            <button
              type="button"
              onClick={() => setSelectedMethod('apple')}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                selectedMethod === 'apple'
                  ? 'border-[#962831] bg-white/10'
                  : 'border-white/20 hover:border-[#962831]'
              }`}
            >
              <Smartphone className="w-5 h-5 mr-3 !text-[#962831]" />
              <span className="!text-white font-medium">Apple Pay</span>
            </button>
          ) : (
            <div className="flex items-center p-4 rounded-lg border-2 border-white/10 bg-white/5 opacity-50 cursor-not-allowed">
              <Smartphone className="w-5 h-5 mr-3 !text-white/30" />
              <span className="!text-white/30 font-medium">Apple Pay (Unavailable)</span>
            </div>
          )}

          {/* Google Pay */}
          {isGooglePayAvailable ? (
            <button
              type="button"
              onClick={() => setSelectedMethod('google')}
              className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                selectedMethod === 'google'
                  ? 'border-[#962831] bg-white/10'
                  : 'border-white/20 hover:border-[#962831]'
              }`}
            >
              <Smartphone className="w-5 h-5 mr-3 !text-[#962831]" />
              <span className="!text-white font-medium">Google Pay</span>
            </button>
          ) : (
            <div className="flex items-center p-4 rounded-lg border-2 border-white/10 bg-white/5 opacity-50 cursor-not-allowed">
              <Smartphone className="w-5 h-5 mr-3 !text-white/30" />
              <span className="!text-white/30 font-medium">Google Pay (Unavailable)</span>
            </div>
          )}
        </div>
      </div>

      {/* Card Form Container - Square SDK attaches here */}
      {/* Always render but hide when not selected to preserve Square card element */}
      <div className={`space-y-2 mb-6 ${selectedMethod === 'card' ? '' : 'hidden'}`}>
        <h4 className="text-md font-medium !text-white">Card Information</h4>
        <div
          id={containerId}
          className="border border-white/20 rounded-lg p-4 bg-white min-h-[120px]"
        />
      </div>

      {/* Consent acknowledgments — both required to pay */}
      <div id="taste-consent-section">
      {/* Pre-Order Acknowledgment — Lia's key recommendation: explicit consent to batch shipping */}
      <div className="mb-4 p-4 bg-amber-950/30 border border-amber-800/50 rounded-xl">
        <div className="flex items-start space-x-3">
          <div className="relative flex items-center justify-center mt-0.5">
            <div
              onClick={() => setAgreedToPreOrder(!agreedToPreOrder)}
              className="w-5 h-5 border-2 rounded transition-all flex items-center justify-center cursor-pointer flex-shrink-0"
              style={{
                backgroundColor: agreedToPreOrder ? '#962831' : 'transparent',
                borderColor: agreedToPreOrder ? '#962831' : (showConsentHint && !agreedToPreOrder ? '#ef4444' : 'rgba(255,255,255,0.3)'),
                borderWidth: '2px'
              }}
            >
              {agreedToPreOrder && (
                <svg className="w-3 h-3" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <label
            className="text-sm !text-white/70 leading-relaxed cursor-pointer"
            onClick={() => setAgreedToPreOrder(!agreedToPreOrder)}
          >
            <strong>I understand this is a pre-order.</strong> My payment will be charged today. My order will ship via UPS 2-Day no later than <strong>{CAMPAIGN.shipDateDisplay}</strong>. I may cancel within 48 hours for a full refund.
          </label>
        </div>
      </div>

      {/* Terms of Sale + Allergen Agreement */}
      <div className="mb-6">
        <div className="flex items-start space-x-3">
          <div className="relative flex items-center justify-center mt-0.5">
            <div
              onClick={() => setAgreedToTerms(!agreedToTerms)}
              className="w-5 h-5 border-2 rounded transition-all flex items-center justify-center cursor-pointer flex-shrink-0"
              style={{
                backgroundColor: agreedToTerms ? '#962831' : 'transparent',
                borderColor: agreedToTerms ? '#962831' : (showConsentHint && !agreedToTerms ? '#ef4444' : 'rgba(255,255,255,0.3)'),
                borderWidth: '2px'
              }}
            >
              {agreedToTerms && (
                <svg className="w-3 h-3" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <label
            className="text-sm !text-white/70 leading-relaxed cursor-pointer"
            onClick={() => setAgreedToTerms(!agreedToTerms)}
          >
            I agree to the{' '}
            <Link
              to="/terms-of-sale"
              className="!text-[#2563EB] hover:!text-[#1D4ED8] underline font-medium transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Sale
            </Link>
            , including the allergen notice, perishable goods disclaimer, and cancellation policy.
          </label>
        </div>
      </div>
      </div>{/* /taste-consent-section */}

      {/* Consent hint — shown when the user tries to pay without both boxes checked */}
      {showConsentHint && (!agreedToPreOrder || !agreedToTerms) && (
        <div className="mb-4 p-3 bg-red-950/40 border border-red-500/60 rounded-lg flex items-start space-x-2">
          <span className="!text-red-300 text-sm font-medium">
            Please confirm the acknowledgment{!agreedToPreOrder && !agreedToTerms ? 's' : ''} above before paying.
          </span>
        </div>
      )}

      {/* Error Display */}
      {paymentError && (
        <div className="bg-red-950/50 border-l-4 border-red-500 !text-red-300 p-4 mb-4 rounded">
          <p className="text-sm !text-inherit">{paymentError}</p>
        </div>
      )}

      {/* Payment Button */}
      <div className="mt-6 space-y-3">
        <button
          type="button"
          onClick={handlePayment}
          disabled={isProcessing}
          aria-disabled={!agreedToTerms || !agreedToPreOrder}
          className={`w-full bg-[#962831] hover:bg-[#6B2A2A] !text-white font-bold py-4 px-4 rounded-xl shadow-lg transform hover:-translate-y-0.5 transition-all duration-150 disabled:cursor-not-allowed flex items-center justify-center ${
            !agreedToTerms || !agreedToPreOrder ? 'opacity-60' : ''
          }`}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <LoadingSpinner size="sm" />
              <span className="ml-2 !text-white">Processing...</span>
            </div>
          ) : (
            <span className="!text-white">{buttonText}</span>
          )}
        </button>

        {/* Security Notice */}
        <div className="text-center text-xs !text-white/40">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="!text-white/60">Secured by Square</span>
          </div>
          <p className="!text-inherit">Your payment information is encrypted and secure.</p>
        </div>
      </div>
    </div>
  )
}

export default TastePaymentForm
